<template>
  <div id="home" class="flex flex-col gap-y-7 mb-7">
    <!--
      UNIFIED HERO PANEL. In the new layout the profile card, the
      "MEANWHILE…" activity row AND the unauth Login / Sign-up footer are ONE
      rounded panel: the profile row sits at the top, the MEANWHILE heading +
      activity avatars row sit BELOW it, and (for logged-out visitors) the grey
      Login / Sign-up FOOTER closes the panel at the bottom. All children render
      `bare` (no panel chrome of their own) and the single panel wrapper here
      draws the rounded border + surface-secondary background.

      The panel renders when ANY child is visible: the profile card is
      auth-gated, the activity row keeps its unauthenticated visibility, and the
      login footer renders for logged-out visitors — so an unauth visitor always
      sees the panel (MEANWHILE row + login footer), and even when the
      CommunityFeed section is hidden the login footer alone still draws the
      panel.
    -->
    <div
      v-if="
        userStore.isAuthenticated ||
        isSectionVisible(ChannelSectionName.CommunityFeed) ||
        isLoginFooterVisible
      "
      class="mx-5 overflow-hidden rounded-[20px] border-2 border-surface-secondary bg-surface-secondary"
      data-testid="channel-hero-panel"
    >
      <ChannelProfileCard
        v-if="userStore.isAuthenticated"
        :bare="true"
        :completed-label="loadingContests ? undefined : questsCompletedLabel"
        data-testid="channel-profile-card-mount"
        @upgrade="onProfileCardUpgrade"
        @see-benefits="onShowBenefits"
      />

      <!--
        MEANWHILE section: the heading leads the activity row INSIDE the panel.
        It carries the panel's bottom padding/divider so the row visually reads
        as the lower half of the same card. The row renders `bare` (no chrome)
        and keeps its viewport-fit slicing.
      -->
      <div
        v-if="isSectionVisible(ChannelSectionName.CommunityFeed)"
        :class="[
          'flex flex-col gap-4 px-6 pb-6 lg:px-8 lg:pb-8',
          userStore.isAuthenticated ? 'border-t border-border pt-6' : 'pt-6',
        ]"
      >
        <ChannelRecentActivityRow
          :bare="true"
          class="h-fit"
          :viewport-fit="true"
        >
          <template #header>
            <h2
              class="text-[19px] font-bold uppercase leading-[22px] text-text"
            >
              {{ $t("channel.recentActivity.meanwhile") }}
            </h2>
          </template>
        </ChannelRecentActivityRow>
      </div>

      <!--
        UNAUTH LOGIN FOOTER. The grey footer that closes the hero panel for
        logged-out visitors: a centered brand pill inside the panel's bottom
        section. It is SECTIONALLY PART OF the panel (same rounded surface), set
        off from the MEANWHILE row above by the top divider — the
        "white MEANWHILE card + grey footer" treatment (the panel's
        surface-secondary fill is the grey #f0f0f0 footer colour).

        The `border-t border-border` divider only renders when a section sits
        above the footer (profile card and/or MEANWHILE row); for a bare
        logged-out visitor whose CommunityFeed section is hidden, the footer is
        the only child and needs no divider.

        Footer geometry: p-[32px] (p-8) centered column; button is a fixed
        357px-wide / 50px-tall brand pill that caps to the available width on
        narrow panels (mobile) via `max-w-full`, so it reads near-full-width with
        side padding on mobile and a centered fixed pill on tablet/desktop — all
        three breakpoints share this one rule. Replaces the legacy
        GetChannelMembershipButton / Skeleton block in the new layout. The embed
        context guard mirrors the membership button: hidden when in
        non-FanfestAuth embed mode.
      -->
      <div
        v-if="isLoginFooterVisible"
        :class="[
          'flex flex-col items-center bg-surface-secondary px-[2px] py-8',
          userStore.isAuthenticated ||
          isSectionVisible(ChannelSectionName.CommunityFeed)
            ? 'border-t border-border'
            : '',
        ]"
      >
        <button
          type="button"
          class="flex h-[50px] w-[357px] max-w-full items-center justify-center rounded-full border-t border-border-button-shadow-light bg-fill-brand px-6 text-[15px] font-medium leading-5 text-text-on-brand transition-colors hover:bg-fill-brand-hover active:bg-fill-brand-pressed"
          data-testid="channel-login-signup-btn"
          @click="userStore.login()"
        >
          {{ $t("channel.buttons.loginSignup") }}
        </button>
      </div>
    </div>

    <!--
      ANNOUNCEMENT CARD. Backed by the latest channel thread: the first line of
      `latestThread.content` becomes the title and the remainder the description.
      The dot count reflects the real thread list length (capped at 4). The card
      is hidden when no threads exist yet (no skeleton — the card only appears
      once the first thread is present). Clicking opens the ANNOUNCEMENTS modal
      (the overlay that hosts the full thread list). The `#threads` anchor
      is kept here so the top-bar ANNOUNCEMENTS tab can scroll-to / activate this
      position.

      isSectionVisible gate is preserved so admins can hide the Threads section
      from the channel sections admin and the card disappears accordingly.
    -->
    <ChannelBenefitsBar
      v-if="
        isSectionVisible(ChannelSectionName.Threads) &&
        latestThreadTitle !== undefined
      "
      id="threads"
      :title="latestThreadTitle"
      :description="latestThreadDescription"
      :dot-count="announcementDotCount"
      :active-index="0"
      @click="onAnnouncementsTrigger"
    />

    <ContestsSection
      v-if="isSectionVisible(ChannelSectionName.Contests)"
      id="contests"
      :contests="contests"
      :loading="loadingContests"
      :loading-more="loadingMoreContests"
      :has-more="hasMoreContests"
      @refetch="refetchContests"
      @load-more="loadMoreContests"
    />

    <ChannelMvpLeaderboardSection
      v-if="isSectionVisible(ChannelSectionName.Leaderboard)"
      :new-layout="true"
    />

    <OffersSection
      v-if="isSectionVisible(ChannelSectionName.Offers) && enableCouponOffers"
      id="offers"
      :offers="offers"
      :loading="loadingOffers"
      :loading-more="loadingMoreOffers"
      :has-more="hasMoreOffers"
      @load-more="loadMoreOffers"
      @refetch="refetchOffers"
    />

    <ShowSection
      v-if="isSectionVisible(ChannelSectionName.Shows)"
      id="shows"
      data-testid="channel-section-shows"
      :shows="shows"
      :loading="loadingShows"
      :loading-more="loadingMoreShows"
      :has-more="hasMoreShows"
      @load-more="loadMoreShows"
      @refetch="refetchShows"
    >
      <template #actions>
        <RouterLink
          :to="{
            name: RouteNames.ShowList,
            params: { channel: channelStore.channelSlug },
          }"
          custom
        >
          <template #default="{ navigate }">
            <ButtonV2 variant="subtle" size="md" class="px-6" @click="navigate">
              {{ $t("channel.buttons.browseAllShows") }}
            </ButtonV2>
          </template>
        </RouterLink>
      </template>
    </ShowSection>
  </div>
</template>

<script setup lang="ts">
/**
 * New channel page body (feature flag ON). Single-column document flow — the new
 * shell (channel-layout-v2.vue) is single-column, so source order == visual
 * order and the legacy `grid-row` token-gating hack is intentionally DROPPED
 * here.
 *
 * Order:
 *   1. HERO PANEL (profile card + MEANWHILE + unauth login footer)
 *   2. ANNOUNCEMENT CARD (ChannelBenefitsBar, thread-backed, #threads anchor)
 *   3. ContestsSection (#contests, QUESTS)
 *   4. ChannelMvpLeaderboardSection
 *   5. OffersSection (#offers, REWARDS)
 *   6. ShowSection (#shows)
 *
 * The GetChannelMembershipButton / Skeleton block is omitted here: in the new
 * layout the unauth CTA is the dedicated Login / Sign-up footer, which matches
 * the single-pill design. Stable anchor ids (`#home`, `#contests`,
 * `#offers`, `#shows`, `#threads`) are the scroll targets for the top-bar tabs.
 *
 * All data / handlers come from `useChannelPage()` (run in channel.vue's setup)
 * via inject; this component is presentation-only.
 */
import { inject } from "vue";
import { ChannelSectionName } from "@/generated/graphql";
import { RouteNames } from "@/router/app.router";
import { ChannelPageKey } from "@/composables/channel/use-channel-page";

import ButtonV2 from "../ui/button/button-v2.vue";
import ChannelRecentActivityRow from "./channel-recent-activity-row.vue";
import ChannelMvpLeaderboardSection from "../leaderboard/channel-mvp-leaderboard-section.vue";
import ShowSection from "./shows/show-section.vue";
import ContestsSection from "@/components/channel/contests/contest-section.vue";
import OffersSection from "@/components/channel/offers/offers-section.vue";
import ChannelBenefitsBar from "./channel-page/channel-benefits-bar.vue";
import ChannelProfileCard from "./channel-page/channel-profile-card.vue";

const {
  userStore,
  channelStore,
  isSectionVisible,
  isLoginFooterVisible,
  loadingContests,
  questsCompletedLabel,
  onProfileCardUpgrade,
  onShowBenefits,
  latestThreadTitle,
  latestThreadDescription,
  announcementDotCount,
  onAnnouncementsTrigger,
  contests,
  loadingMoreContests,
  hasMoreContests,
  refetchContests,
  loadMoreContests,
  enableCouponOffers,
  offers,
  loadingOffers,
  loadingMoreOffers,
  hasMoreOffers,
  loadMoreOffers,
  refetchOffers,
  shows,
  loadingShows,
  loadingMoreShows,
  hasMoreShows,
  loadMoreShows,
  refetchShows,
} = inject(ChannelPageKey)!;
</script>
